import { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LoginPage } from "./pages/LoginPage";
import { Layout } from "./components/Layout";
import { PublicLayout } from "./components/PublicLayout";
import { InstallPrompt } from "./components/InstallPrompt";
import { Toaster } from "sonner";
import { registerServiceWorker } from "./lib/push";

// Authenticated app pages
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const TasksPage = lazy(() => import("./pages/TasksPage"));
const CourtesyCallsPage = lazy(() => import("./pages/CourtesyCallsPage"));
const GuestRelationsPage = lazy(() => import("./pages/GuestRelationsPage"));
const ChecklistsPage = lazy(() => import("./pages/ChecklistsPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const SOPsPage = lazy(() => import("./pages/SOPsPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const PlatformPage = lazy(() => import("./pages/PlatformPage"));
const GuestAdminPage = lazy(() => import("./pages/GuestAdminPage"));
const AIVisibilityPage = lazy(() => import("./pages/AIVisibilityPage"));
const LostFoundPage = lazy(() => import("./pages/LostFoundPage"));
const HandoverPage = lazy(() => import("./pages/HandoverPage"));
const AnnouncementsPage = lazy(() => import("./pages/AnnouncementsPage"));

// Public marketing pages
const LandingPage = lazy(() => import("./pages/public/LandingPage"));
const FeaturesPage = lazy(() => import("./pages/public/FeaturesPage"));
const PricingPage = lazy(() => import("./pages/public/PricingPage"));
const ContactPage = lazy(() => import("./pages/public/ContactPage"));
const SignupPage = lazy(() => import("./pages/public/SignupPage"));
const LegalPage = lazy(() => import("./pages/public/LegalPage"));
const GuestPortalPage = lazy(() => import("./pages/public/GuestPortalPage"));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
      <p className="text-muted-foreground">Yükleniyor...</p>
    </div>
  </div>
);

const FullScreenLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center">
      <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <h1 className="text-2xl font-bold mb-2">FlowUpy</h1>
      <p className="text-muted-foreground">Yükleniyor...</p>
    </div>
  </div>
);

// Authenticated hotel app route (org admin / staff)
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin, isSuperadmin } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (isSuperadmin) return <Navigate to="/platform" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

// Public page wrapper
const PublicRoute = ({ children }) => (
  <PublicLayout>
    <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
  </PublicLayout>
);

// Smart home: landing for guests, dashboard for org users, platform for superadmin
const HomeRoute = () => {
  const { user, loading, isSuperadmin } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (isSuperadmin) return <Navigate to="/platform" replace />;
  if (user) {
    return (
      <Layout>
        <Suspense fallback={<LoadingSpinner />}><DashboardPage /></Suspense>
      </Layout>
    );
  }
  return <PublicRoute><LandingPage /></PublicRoute>;
};

const AppRoutes = () => {
  const { user, loading, isSuperadmin } = useAuth();
  
  // Register service worker once on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);
  
  if (loading) return <FullScreenLoader />;

  const appRoute = (Component, adminOnly = false) => (
    <ProtectedRoute adminOnly={adminOnly}>
      <Suspense fallback={<LoadingSpinner />}><Component /></Suspense>
    </ProtectedRoute>
  );

  return (
    <Routes>
      {/* Home (smart) */}
      <Route path="/" element={<HomeRoute />} />

      {/* Guest portal (public, QR-based, no navbar) */}
      <Route path="/g/:orgCode" element={<Suspense fallback={<LoadingSpinner />}><GuestPortalPage /></Suspense>} />

      {/* Public marketing */}
      <Route path="/ozellikler" element={<PublicRoute><FeaturesPage /></PublicRoute>} />
      <Route path="/fiyatlandirma" element={<PublicRoute><PricingPage /></PublicRoute>} />
      <Route path="/iletisim" element={<PublicRoute><ContactPage /></PublicRoute>} />
      <Route path="/kvkk" element={<PublicRoute><LegalPage docType="kvkk" /></PublicRoute>} />
      <Route path="/gizlilik" element={<PublicRoute><LegalPage docType="privacy" /></PublicRoute>} />
      <Route path="/mesafeli-satis" element={<PublicRoute><LegalPage docType="distance_sales" /></PublicRoute>} />
      <Route path="/iade" element={<PublicRoute><LegalPage docType="refund" /></PublicRoute>} />
      <Route path="/cerez" element={<PublicRoute><LegalPage docType="cookie" /></PublicRoute>} />

      {/* Auth */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/kayit" element={user ? <Navigate to="/" replace /> : <PublicRoute><SignupPage /></PublicRoute>} />

      {/* Platform (superadmin) */}
      <Route
        path="/platform"
        element={
          loading ? <FullScreenLoader /> :
          !user ? <Navigate to="/login" replace /> :
          !isSuperadmin ? <Navigate to="/" replace /> :
          <Suspense fallback={<LoadingSpinner />}><PlatformPage /></Suspense>
        }
      />

      {/* Authenticated hotel app */}
      <Route path="/tasks" element={appRoute(TasksPage)} />
      <Route path="/courtesy-calls" element={appRoute(CourtesyCallsPage)} />
      <Route path="/guest-relations" element={appRoute(GuestRelationsPage)} />
      <Route path="/checklists" element={appRoute(ChecklistsPage)} />
      <Route path="/projects" element={appRoute(ProjectsPage)} />
      <Route path="/sops" element={appRoute(SOPsPage)} />
      <Route path="/guest-admin" element={appRoute(GuestAdminPage)} />
      <Route path="/lost-found" element={appRoute(LostFoundPage)} />
      <Route path="/handover" element={appRoute(HandoverPage)} />
      <Route path="/announcements" element={appRoute(AnnouncementsPage)} />
      <Route path="/ai-visibility" element={appRoute(AIVisibilityPage, true)} />
      <Route path="/reports" element={appRoute(ReportsPage, true)} />
      <Route path="/admin" element={appRoute(AdminPage, true)} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <InstallPrompt />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: 'hsl(var(--card))',
                  color: 'hsl(var(--foreground))',
                  border: '1px solid hsl(var(--border))',
                },
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
